/**
 * NuxtImg `sizes` for the CategoryHero tiles StaticCategories lays out,
 * derived from the same Tailwind classes that size the grid. Every screen key
 * is spelled out: an unprefixed value becomes a 1px screen and adds 1w/2w
 * candidates (BB-160), and leaving keys out drops the larger widths.
 */

/** Tailwind breakpoint prefix → NuxtImg screen key (module defaults), smallest first. */
const SCREENS = [
	{ prefix: "", key: "xs" },
	{ prefix: "sm", key: "sm" },
	{ prefix: "md", key: "md" },
	{ prefix: "lg", key: "lg" },
	{ prefix: "xl", key: "xl" },
	{ prefix: "2xl", key: "xxl" }
] as const;

/**
 * The grid StaticCategories renders when the blok sets no `items_in_row`
 * (`listClasses` there). Kept as a copy: Tailwind scans only `.vue` files.
 */
const DEFAULT_CATEGORY_GRID = "md:grid-cols-4 grid-cols-2";

const GRID_COLS = /^(?:(sm|md|lg|xl|2xl):)?grid-cols-(\d+)$/;

/** A scrollable row's tiles are `w-32 sm:w-36`, then `md:flex-1` shares the row. */
const SCROLLABLE_TILE_WIDTHS: Readonly<Record<string, string>> = {
	xs: "128px",
	sm: "144px"
};

export interface CategoryTileLayout {
	/** `blok.items_in_row`: Tailwind grid classes, e.g. `md:grid-cols-4 grid-cols-2`. */
	itemsInRow?: string;
	/** `blok.scrollable`: one flex row instead of a grid. */
	scrollable?: boolean;
	/** Tiles in the row; a scrollable row splits its width between them from md. */
	itemCount?: number;
}

const share = (columns: number) => `${Math.ceil(100 / Math.max(columns, 1))}vw`;

const gridColumns = (classes: string): Record<string, number> =>
	Object.fromEntries(
		classes.split(/\s+/).flatMap((token) => {
			const match = GRID_COLS.exec(token);
			return match ? [[match[1] ?? "", Number(match[2])]] : [];
		})
	);

/** A grid without `grid-cols-*` is one column; each breakpoint keeps the last count set. */
const gridSizes = (classes: string) => {
	const columns = gridColumns(classes);
	return SCREENS.map(({ key }, index) => {
		const count = SCREENS.slice(0, index + 1).reduce(
			(current, { prefix }) => columns[prefix] ?? current,
			1
		);
		return `${key}:${share(count)}`;
	}).join(" ");
};

const scrollableSizes = (itemCount: number) =>
	SCREENS.map(
		({ key }) => `${key}:${SCROLLABLE_TILE_WIDTHS[key] ?? share(itemCount)}`
	).join(" ");

export const categoryTileSizes = ({
	itemsInRow,
	scrollable = false,
	itemCount = 1
}: CategoryTileLayout = {}): string =>
	scrollable
		? scrollableSizes(itemCount)
		: gridSizes(itemsInRow || DEFAULT_CATEGORY_GRID);

/** `sizes` for the default grid; CategoryHero's fallback. */
export const CATEGORY_TILE_SIZES = categoryTileSizes();

/**
 * NuxtImg `densities` for the tiles. @nuxt/image sizes each candidate at the
 * lower edge of its screen range, so with only 1x and 2x the largest is 768w,
 * short of a 2x tile in a two-column row just below lg (about 970 px).
 */
export const CATEGORY_TILE_DENSITIES = "x1 x2 x3";
